import moment from 'moment'
import { Checkbox } from '@material-ui/core'
import { AssignmentInd as LeadIcon, Assignment as IssueIcon } from '@material-ui/icons'
import { ListItem, Text } from 'shared/ui'
import { LinkLabel } from './linkResolver'

// One task in the navbar's "Today's Tasks" popup (src/navigation/TodayPopover). Deliberately
// NOT a reuse of TaskAgendaItem.js — that component dispatches into the calendar module's
// own isolated Redux store (useDispatch/updateTask from ../store/actions), but this popup
// renders from src/navigation/, outside that module's <Provider>. So this row is presentational
// only: the checkbox reflects task.status and, when the parent passes `onComplete`, ticking
// it calls that (the parent does the plain-API update) — no dispatch in here. A completed
// task's checkbox is disabled (one-way: this popup only completes tasks, never reopens).
// Row 1: name — due date/alert time (the two share one plain-text area, no icon, same color
// as each other). Row 2 (only when present): linked item, icon+text — vocabulary borrowed
// from TaskAgendaItem.js's LINK_META/linkText. Overdue (due before today, not done) turns
// everything in both rows red via `color="error"`, since TodayPopover's due_end fetch now
// mixes overdue tasks in alongside today's and that's the one thing that needs to stand out.
const LINK_META = {
  'lead/Lead': { Icon: LeadIcon },
  'project/Issue': { Icon: IssueIcon },
}

const dueLabel = (date) => {
  const d = moment(date)
  if (d.isSame(moment(), 'day')) return 'Today'
  if (d.isSame(moment().add(1, 'day'), 'day')) return 'Tomorrow'
  if (d.isSame(moment().subtract(1, 'day'), 'day')) return 'Yesterday'
  return d.format('D MMM YYYY')
}


// Checkbox (20px, size="small" + padding:0) plus its 4px gap to the name — row 2 pads by
// this much so it starts under the name, not the checkbox.
const ROW2_INDENT = 24

function TaskRow({ task, onClick, onComplete }) {
  const isDone = task?.status === 'completed'
  const isOverdue = !isDone && task?.due_at && moment(task.due_at).isBefore(moment(), 'day')
  const link = (task?.links || []).find(l => LINK_META[l?.target_object_type])
  const LinkIcon = link && LINK_META[link.target_object_type].Icon

  const dueAlertText = [
    task?.due_at && dueLabel(task.due_at),
    task?.alert_at && moment(task.alert_at).format('HH:mm'),
  ].filter(Boolean).join(', ')

  // shared/ui ListItem (default "button" variant) renders through react-router's <Link>
  // with `to={undefined}`; after our onClick runs, the Link still fires navigate() →
  // history.push(undefined), which wipes the query string showSideBar just set — so the
  // task-detail sidebar never stays open. Same guard as EventRow.js's own handleClick.
  const handleClick = (e) => {
    if (e) { e.preventDefault(); e.stopPropagation() }
    onClick && onClick()
  }

  return (
    <ListItem
      noGutter
      onClick={handleClick}
      primary={
        // Flat row (not checkbox+name nested inside their own div) so `alignItems: 'baseline'`
        // lines up the name's first-line baseline with the due/alert text's baseline directly
        // — nesting them would align the *name div's* box against the due/alert text instead,
        // which is what previously left the (larger, body1) name looking baseline-low against
        // the (smaller, body2) due/alert text.
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
          {/* baseline-aligned row (see above) sits the checkbox flush with the text baseline —
              nudge it down a couple px so it doesn't look like it's floating above the name */}
          <Checkbox
            checked={isDone}
            disabled={isDone || !onComplete}
            onChange={() => onComplete && onComplete()}
            // The row is a <Link> (see handleClick above) — keep a checkbox click from also
            // opening the task sidebar or letting the anchor navigate (preventDefault).
            onClick={(e) => { e.preventDefault(); e.stopPropagation() }}
            color="primary"
            size="small"
            style={{ padding: 0, position: 'relative', top: 4 }}
          />
          <Text
            color={isOverdue ? 'error' : undefined}
            style={{
              fontWeight: 500,
              textDecoration: isDone ? 'line-through' : undefined,
              wordBreak: 'break-word',
              flex: '1 1 auto',
              minWidth: 0,
            }}
          >
            {task?.name}
          </Text>
          {dueAlertText && (
            <Text
              variant="body2"
              color={isOverdue ? 'error' : 'textMuted'}
              style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              {dueAlertText}
            </Text>
          )}
        </div>
      }
      secondary={
        link ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2, paddingLeft: ROW2_INDENT, minWidth: 0 }}>
            <LinkIcon color={isOverdue ? 'error' : 'disabled'} style={{ fontSize: 14, flexShrink: 0 }} />
            <Text
              variant="body2"
              color={isOverdue ? 'error' : 'textMuted'}
              style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              <LinkLabel link={link} />
            </Text>
          </div>
        ) : undefined
      }
    />
  )
}

export default TaskRow
